import { WarningIcon } from '@/common/icons';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/numberFormat';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { FunctionComponent, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

interface MainCardProps {
  title: string;
  Icon?: FunctionComponent<
    SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
  value?: number;
  unit?: string;
  percentage?: number;
  iconBgColor?: string;
  iconColor?: string;
  statusPercentage?: 'up' | 'down';
  isLoading?: boolean;
  className?: string;
  onClick?: () => void;
  /** Ixtiyoriy: belgilansa, hover qilganda tooltip ko'rsatiladi */
  tooltipText?: string;
  textLinearColor?: string;
  preValue?: number;
  warningIconColor?: string;
  leftBorderColor?: string;
}

export const MainCard = ({
  unit,
  Icon,
  title,
  value,
  iconColor,
  isLoading,
  className,
  percentage,
  iconBgColor,
  statusPercentage,
  onClick,
  tooltipText,
  textLinearColor,
  preValue,
  warningIconColor,
  leftBorderColor,
}: MainCardProps) => {
  const { t } = useTranslation();

  const titleNode = tooltipText ? (
    <div className="flex items-center gap-x-2.5 pr-3">
      <div className="text-base font-medium text-foreground">{title}</div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <WarningIcon
                className={cn('h-5 w-5', warningIconColor || 'text-white')}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  ) : (
    <div className="pr-3 text-base font-medium text-foreground">{title}</div>
  );

  return (
    <div
      className={cn(
        'flex h-full flex-col gap-y-4 rounded-sm border border-border bg-card px-4 py-3',
        className,
      )}
      style={{
        borderLeft: leftBorderColor
          ? `4px solid ${leftBorderColor}`
          : undefined,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        {titleNode}
        {Icon && (
          <div
            onClick={onClick}
            className={cn(
              `flex h-8 w-8 min-w-8 items-center justify-center rounded-[6.67px] border border-white/20 ${!!onClick ? 'cursor-pointer' : ''}`,
              iconBgColor || 'bg-green-150',
            )}
          >
            <Icon className={cn('h-4 w-4', iconColor || 'text-[#A185FD]')} />
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="h-[48px] w-[60%] animate-pulse rounded-md bg-muted" />
      ) : value !== undefined && value !== null ? (
        <div className="flex items-center gap-3">
          {preValue && (
            <>
              <div className="text-xl font-bold text-foreground">
                {formatNumber(preValue)}
              </div>
              <div className="text-base font-medium text-muted-foreground">
                {`${unit}dan`}
              </div>
            </>
          )}
          <div
            className={`text-5xl font-bold max-[1920px]:text-3xl ${textLinearColor ? textLinearColor : 'text-foreground'}`}
          >
            {formatNumber(value)}
          </div>
          <div className="text-base font-medium text-muted-foreground">
            {unit}
          </div>
        </div>
      ) : (
        <div
          className={`text-5xl font-bold ${textLinearColor ? textLinearColor : 'text-foreground'}`}
        >
          -
        </div>
      )}

      {percentage && (
        <div className="flex items-center gap-2">
          <div className="text-sm font-normal text-muted-foreground">
            {t('compared_last_year')}:
          </div>
          <div
            className={cn(
              'flex items-center gap-2 rounded-[100px] px-2 py-0.5',
              statusPercentage === 'up' ? 'bg-green-950' : 'bg-red-950',
            )}
          >
            {statusPercentage === 'up' ? (
              <ArrowUp className={cn('h-4 w-4 text-green-500')} />
            ) : (
              <ArrowDown className={cn('h-4 w-4 text-red-500')} />
            )}
            <div
              className={cn(
                'text-sm font-medium',
                statusPercentage === 'up' ? 'text-green-500' : 'text-red-500',
              )}
            >
              {percentage}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
